import { z } from "zod";
import { LessonId } from "./room.ts";
import { SlideSnapshot } from "./slides.ts";

/**
 * Версия формата снимка доски.
 *
 * Это не украшение: формат уже сменился один раз, и старые строки никто не
 * переписывал. Читатель обязан смотреть на поле, а не считать, что формат
 * один навсегда.
 *
 * 1: плоский список элементов, доска была одна на урок.
 * 2: список слайдов, у каждого свои элементы.
 */
export const BOARD_SNAPSHOT_FORMAT_FLAT = 1;
export const BOARD_SNAPSHOT_FORMAT = 2;

/**
 * Ключ верхнего уровня в Y.Doc урока, под которым лежала единственная доска
 * урока до появления слайдов.
 *
 * Теперь элементы живут в аннотациях слайда (см. slides.ts), а этот ключ
 * остаётся источником для переноса: при первом открытии старого урока его
 * содержимое копируется на первый слайд. Копируется, а не переносится:
 * исходник остаётся нетронутым, и это дешёвая страховка на случай, если
 * перенос окажется неверным.
 *
 * Живёт здесь, потому что читают его двое: клиент в браузере и сборщик
 * снимков на сервере. Разъехавшиеся строковые литералы дали бы пустой архив
 * при полностью рабочей доске, и заметил бы это только пользователь.
 */
export const BOARD_ELEMENTS_KEY = "board.elements";

export const BoardSnapshotSource = z.enum(["unload", "end", "periodic", "repair"]);
export type BoardSnapshotSource = z.infer<typeof BoardSnapshotSource>;

export const BoardSnapshotResponse = z.object({
  /**
   * Формат хранения, как он лежит в базе. Врать здесь нельзя: по этому полю
   * видно, снимался снимок до слайдов или после.
   */
  format: z.union([z.literal(BOARD_SNAPSHOT_FORMAT_FLAT), z.literal(BOARD_SNAPSHOT_FORMAT)]),
  lessonId: LessonId,
  takenAt: z.number().int(),
  elementCount: z.number().int(),
  /**
   * Слайды приходят ВСЕГДА, в том числе для снимков формата 1: сервер
   * заворачивает плоский список в один слайд. Просмотрщику архива тогда не
   * нужны две ветки отрисовки, а старые снимки продолжают открываться.
   */
  slides: z.array(SlideSnapshot),
  /**
   * Плоский список элементов. Для формата 2 это элементы первого слайда.
   *
   * ⚠️ Поле ЖИВОЕ, хотя заводилось как временное. Его читает `FlatBoard` в
   * `ArchivedBoard.tsx`, то есть единственная ветка, которая показывает
   * снимки формата 1, а их в базе большинство. Убирать поле можно только
   * после того, как эта ветка переедет на `slides[0].elements`, и именно в
   * таком порядке: сначала выкатить клиента, потом убрать поле.
   *
   * Элементы Excalidraw zod-схемой НЕ валидируем намеренно.
   * OrderedExcalidrawElement это сотни строк типов, которые меняются с каждым
   * релизом @next. Схема, повторяющая их, ломалась бы на каждом обновлении
   * доски и при этом не защищала бы ни от чего: данные пришли из нашего же
   * Y.Doc, а рисует их тот же Excalidraw, который их и породил.
   */
  elements: z.array(z.record(z.string(), z.unknown())),
  appState: z.object({ viewBackgroundColor: z.string().optional() }),
});
export type BoardSnapshotResponse = z.infer<typeof BoardSnapshotResponse>;

/**
 * Тикет доски. Приходит вместо токена LiveKit, и это главный смысл замены:
 * при недоступном SFU клиент раньше не получал токен, а вместе с ним терял и
 * доску, то есть четвёртый уровень лестницы деградации падал вместе с видео.
 */
export const BoardTicketResponse = z.object({
  ticket: z.string().min(1),
  lessonId: LessonId,
  /** Адрес Hocuspocus приходит с сервера по той же причине, что и URL SFU. */
  collabUrl: z.string().url(),
  expiresAt: z.number().int(),
  /**
   * Документ, на который выписан пропуск. Эхо поля запроса.
   *
   * ⚠️ Необязательное: старый сервер его не присылает, и тогда документ это
   * доска занятия, как было всегда. Клиент на нём ничего не строит, поле нужно
   * отладке: перепутанный документ иначе виден только липким отказом
   * подключения без единой подсказки, к чему именно просили пропуск.
   */
  doc: z.string().uuid().optional(),
});
export type BoardTicketResponse = z.infer<typeof BoardTicketResponse>;

/**
 * Тело запроса пропуска: на КАКОЙ документ занятия он нужен.
 *
 * ⚠️ **Документов у занятия стало больше одного** (`answersDocIdOf` в
 * `./room.ts`): доска общая, а ответы у каждого свои. Пропуск при этом
 * по-прежнему один на документ, потому что `verifyBoardTicket` сверяет поле
 * `lid` с именем документа, и один пропуск на два имени невыразим.
 *
 * ⚠️ **Поле необязательное, и отсутствие означает доску занятия.** Так старый
 * бандл (он тела не шлёт вовсе) продолжает получать ровно то, что получал, а
 * занятие 1:1 не платит за разделение ничем: там доска и ответы это одно имя.
 *
 * Кому что можно, решает ручка, а не эта схема: ученику свой документ ответов и
 * общая доска, преподавателю любой документ его комнаты.
 */
export const BoardTicketRequest = z.object({
  doc: z.string().uuid().optional(),
});
export type BoardTicketRequest = z.infer<typeof BoardTicketRequest>;

/**
 * Порядок по z у элементов доски.
 *
 * Живёт здесь, а не в apps/web, потому что тем же порядком собирается снимок
 * архива на apps/collab. Две копии компаратора однажды разъедутся, и слои в
 * архиве встанут не так, как их видел преподаватель на уроке. Разойдутся они
 * молча и заметит это ученик.
 *
 * Дробный индекс Excalidraw сортируется как строка. id это тай-брейк: без него
 * элементы с равным индексом получают разный порядок у разных клиентов.
 */
export interface ZOrdered {
  id: string;
  index?: string | null | undefined;
}

export function compareZ(a: ZOrdered, b: ZOrdered): number {
  const ai: string = a.index ?? "";
  const bi: string = b.index ?? "";
  if (ai !== bi) return ai < bi ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
